import React from 'react'
import classNames from 'classnames'
import { CalendarIcon, ClockIcon, ShipmentsIcon, TasksIcon } from 'transfix-icons/build'
import { makeStyles, Typography } from 'transfix-ui/core'
import { IIssueAutomationRuleNodeTypeEnum } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { brandColors } from 'transfix-ui'
import { MAX_SHIPMENT_INFO_NODES } from '../constants'

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1),
  },
  title: {
    color: brandColors.coolGray5,
  },
  triggerSection: {
    display: 'flex',
    marginTop: theme.spacing(1.5),
    alignItems: 'center',
    cursor: 'pointer',
  },
  disabledTriggerSection: {
    cursor: 'not-allowed',
    '& > h6': {
      color: brandColors.coolGray5,
    },
  },
  iconContainer: {
    display: 'flex',
    backgroundColor: brandColors.skyBlue1,
    marginRight: theme.spacing(1),
    borderRadius: 5,
  },
  disabledIconContainer: {
    backgroundColor: brandColors.coolGray2,
  },
}))

export interface ISelectTriggerEventProps {
  onSelect: (selectedNode: IIssueAutomationRuleNodeTypeEnum) => void
  selectedNodeTypes: IIssueAutomationRuleNodeTypeEnum[]
}

const SelectTriggerEvent = ({ onSelect, selectedNodeTypes }: ISelectTriggerEventProps) => {
  const classes = useStyles()
  const isMpShipmentIssueAutomationM2Enabled = useFeatureFlag('mpShipmentIssueAutomationM2')
  const isMpShipmentIssueAutomationM5Enabled = useFeatureFlag('mpShipmentIssueAutomationM5')

  const handleClickShipmentStatus = () => {
    onSelect(IIssueAutomationRuleNodeTypeEnum.shipment_status)
  }

  const handleClickTimeWindow = () => {
    onSelect(IIssueAutomationRuleNodeTypeEnum.time_window)
  }

  const handleClickShipmentInformation = () => {
    onSelect(IIssueAutomationRuleNodeTypeEnum.shipment_information)
  }

  const handleClickShipmentEvent = () => {
    onSelect(IIssueAutomationRuleNodeTypeEnum.shipment_event)
  }

  const isAddingShipmentStatus = selectedNodeTypes.includes(
    IIssueAutomationRuleNodeTypeEnum.shipment_status
  )
  const isAddingTimeWindow = selectedNodeTypes.includes(
    IIssueAutomationRuleNodeTypeEnum.time_window
  )
  const isAddingShipmentEvent = selectedNodeTypes.includes(
    IIssueAutomationRuleNodeTypeEnum.shipment_event
  )
  const canAddShipmentInformation =
    selectedNodeTypes.filter(
      nodes => nodes === IIssueAutomationRuleNodeTypeEnum.shipment_information
    ).length < MAX_SHIPMENT_INFO_NODES

  return (
    <div className={classes.container}>
      <Typography variant='overline' className={classes.title}>
        Select Trigger Event
      </Typography>
      <div
        className={classNames({
          [classes.triggerSection]: true,
          [classes.disabledTriggerSection]: isAddingShipmentStatus,
        })}
        onClick={handleClickShipmentStatus}
        tabIndex={0}
        role='button'
        onKeyPress={handleClickShipmentStatus}>
        <div
          className={classNames({
            [classes.iconContainer]: true,
            [classes.disabledIconContainer]: isAddingShipmentStatus,
          })}>
          <ShipmentsIcon
            className='inline'
            size='xlarge'
            color={isAddingShipmentStatus ? 'coolGray5' : 'skyBlue6'}
          />
        </div>
        <Typography variant='subtitle2'>Shipment Status</Typography>
      </div>
      {isMpShipmentIssueAutomationM2Enabled && (
        <div
          className={classNames({
            [classes.triggerSection]: true,
            [classes.disabledTriggerSection]: isAddingTimeWindow,
          })}
          onClick={handleClickTimeWindow}
          tabIndex={0}
          role='button'
          onKeyPress={handleClickTimeWindow}>
          <div
            className={classNames({
              [classes.iconContainer]: true,
              [classes.disabledTriggerSection]: isAddingTimeWindow,
            })}>
            <ClockIcon
              className='inline'
              size='xlarge'
              color={isAddingTimeWindow ? 'coolGray5' : 'skyBlue6'}
            />
          </div>
          <Typography variant='subtitle2'>Time Window</Typography>
        </div>
      )}
      {isMpShipmentIssueAutomationM2Enabled && (
        <div
          className={classNames({
            [classes.triggerSection]: true,
            [classes.disabledTriggerSection]: !canAddShipmentInformation,
          })}
          onClick={handleClickShipmentInformation}
          tabIndex={0}
          role='button'
          onKeyPress={handleClickShipmentInformation}>
          <div
            className={classNames({
              [classes.iconContainer]: true,
              [classes.disabledTriggerSection]: !canAddShipmentInformation,
            })}>
            <TasksIcon
              className='inline'
              size='xlarge'
              color={!canAddShipmentInformation ? 'coolGray5' : 'skyBlue6'}
            />
          </div>
          <Typography variant='subtitle2'>Shipment Information</Typography>
        </div>
      )}
      {isMpShipmentIssueAutomationM5Enabled && (
        <div
          className={classNames({
            [classes.triggerSection]: true,
            [classes.disabledTriggerSection]: isAddingShipmentEvent,
          })}
          onClick={handleClickShipmentEvent}
          tabIndex={0}
          role='button'
          onKeyPress={handleClickShipmentEvent}>
          <div
            className={classNames({
              [classes.iconContainer]: true,
              [classes.disabledTriggerSection]: isAddingShipmentEvent,
            })}>
            <CalendarIcon
              className='inline'
              size='xlarge'
              color={isAddingShipmentEvent ? 'coolGray5' : 'skyBlue6'}
            />
          </div>
          <Typography variant='subtitle2'>Shipment Event</Typography>
        </div>
      )}
    </div>
  )
}

export default SelectTriggerEvent
